/**
 * Onboarding CV Health step (web): the twin of `StepCvHealth.tsx`.
 * Behaviour lives in `use-onboarding-cv-health.ts`; this file only renders,
 * as plain DOM on the onboarding web scaffold (560 column, bare back
 * chevron, the sibling steps' `WebSaveButton` CTA) with CSS variables and
 * logical properties only.
 *
 *   - checking: the four checking steps under a wavy line of real progress,
 *     in a card at the score card's place (it swaps in without a jump); no
 *     back
 *   - failed: the resting heart, "Try again", "Continue without checking"
 *   - unreadable: the unreadable copy, "Try another file", "Continue
 *     without checking"
 *   - report / fixing / fixed: ONE layout and ONE card (the score card with
 *     its gauge, the lines under it, "View individual suggestions" on the
 *     report, "What we checked") so the gauge moves from the import score to
 *     the fixed one on the card the user was watching. While the agent works
 *     (the fix round, an Undo) only the card and the agent line under it
 *     stay: no capsule, no lists, no footer, no checklist.
 *     Inside the report an audience's tabs replace the overview, read-only.
 *     Two ways on: the card's "Fix my CV" when anything is fixable (else the
 *     footer's solid Continue, which runs the same round), and the footer's
 *     quiet Skip.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { ChevronLeft } from 'lucide-react';
import { HealthReportHeader, HealthStatusLine } from '@/components/cv-health';
import { HealthAgentStatus } from '@/components/cv-health/HealthAgentStatus.web';
import { HealthAreaReport } from '@/components/cv-health/HealthAreaReport.web';
import { HealthCheckProgress } from '@/components/cv-health/HealthCheckProgress.web';
import { HealthCheckedAreas } from '@/components/cv-health/HealthCheckedAreas.web';
import { SUGGESTIONS_TEST_ID } from '@/components/cv-health/health-disclosure-memory';
import { HealthDisclosure } from '@/components/cv-health/HealthDisclosure.web';
import { HealthScoreCard } from '@/components/cv-health/HealthScoreCard.web';
import { HealthSuggestionList } from '@/components/cv-health/HealthSuggestionList.web';
import { MaterialSymbol } from '@/components/icons/MaterialSymbol';
import { useLocale } from '@/hooks/useLocale';
import { WebSaveButton } from '../cv/forms/web-form-elements';
import { useOnboardingCvHealth } from './use-onboarding-cv-health';

const RING_SIZE = 168;
const REPORT_HISTORY_KEY = 'onboarding';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function TopBar({
  title,
  onBack,
  backLabel,
}: {
  /** Read by screen readers; the card and the checking steps show "CV Health" themselves. */
  title: string;
  onBack?: () => void;
  backLabel: string;
}) {
  const { isRTL } = useLocale();
  return (
    <div style={{ ...columnStyle, ...topBarStyle }}>
      {onBack ? (
        <button type="button" onClick={onBack} aria-label={backLabel} style={backButtonStyle}>
          <ChevronLeft
            size={24}
            color="var(--text-primary)"
            style={isRTL ? { transform: 'scaleX(-1)' } : undefined}
          />
        </button>
      ) : (
        <span aria-hidden />
      )}
      <h1 className="tabbio-health-sr" style={titleStyle}>
        {title}
      </h1>
      <span aria-hidden />
    </div>
  );
}

function QuietLink({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={disabled ? { ...quietLinkStyle, ...quietLinkDisabledStyle } : quietLinkStyle}
    >
      {label}
    </button>
  );
}

export function StepCvHealth() {
  const reduceMotion = usePrefersReducedMotion();
  const health = useOnboardingCvHealth({ reduceMotion });

  if (health.view === 'hidden') return null;

  const topBar = (
    <TopBar
      title={health.title}
      onBack={health.canGoBack ? health.goBack : undefined}
      backLabel={health.copy.back}
    />
  );

  if (health.view === 'checking') {
    return (
      <div style={rootStyle}>
        {topBar}
        {/* The report's column and position: the score card replaces the
            checking card in place, so the hand-off never jumps. */}
        <div style={scrollStyle}>
          <div style={{ ...columnStyle, ...reportStackStyle }}>
            <HealthCheckProgress
              report={health.checkingReport}
              stage={health.stage}
              showIdentity
              showUnchangedNote
            />
          </div>
        </div>
      </div>
    );
  }

  if (health.view === 'failed') {
    return (
      <div style={rootStyle}>
        {topBar}
        <div className="tabbio-health" style={centerStyle}>
          <HealthStatusLine text={health.copy.failed} working={false} />
        </div>
        <div style={{ ...columnStyle, ...footerStyle }}>
          <WebSaveButton label={health.copy.tryAgain} onClick={health.retry} />
          <QuietLink label={health.copy.continueWithout} onClick={health.continueWithoutChecking} />
        </div>
      </div>
    );
  }

  if (health.view === 'unreadable' && health.report) {
    return (
      <div style={rootStyle}>
        {topBar}
        <div style={centerStyle}>
          <div style={columnStyle}>
            <HealthReportHeader report={health.report} mode="unavailable" size={RING_SIZE} />
          </div>
        </div>
        <div style={{ ...columnStyle, ...footerStyle }}>
          <WebSaveButton label={health.copy.tryAnotherFile} onClick={health.tryAnotherFile} />
          <QuietLink label={health.copy.continueWithout} onClick={health.continueWithoutChecking} />
        </div>
      </div>
    );
  }

  // Report, fixing (the agent line under the card) and fixed, or the fetch
  // after a remount (same chrome, empty body). One tree and one card, so the
  // gauge the user watched moves to the new score in place.
  const { report, footer, reportView } = health;
  const fixed = health.view === 'fixed';
  const working = health.view === 'fixing';

  let body = null;
  if (report && reportView === 'area') {
    body = (
      <HealthAreaReport
        report={report}
        area={health.area}
        variant="onboarding"
        onAreaChange={health.setArea}
        onBack={health.backToOverview}
      />
    );
  } else if (report) {
    body = (
      <div className="tabbio-health-report">
        <HealthScoreCard
          report={report}
          showIdentity
          historyKey={REPORT_HISTORY_KEY}
          action={health.cardAction}
          fromScore={fixed ? health.fromScore : undefined}
        />
        {health.agent ? (
          <HealthAgentStatus
            label={health.agent.label}
            context={health.agent.context}
            working
          />
        ) : null}
        {health.resultHeading ? (
          <h2 style={resultHeadingStyle} aria-live="polite">
            {health.resultHeading}
          </h2>
        ) : null}
        {health.fixesLine ? (
          <p className="tabbio-health-note" style={lineStyle}>
            {health.fixesLine}
          </p>
        ) : null}
        {health.savedText ? (
          <div className="tabbio-health-notice" role="status">
            <MaterialSymbol name="check" size={16} />
            <span>{health.savedText}</span>
            {health.undo ? (
              <button
                type="button"
                className="tabbio-health-btn is-text"
                onClick={health.undo}
                disabled={health.undoing}
                aria-busy={health.undoing || undefined}
              >
                {health.copy.undo}
              </button>
            ) : null}
          </div>
        ) : null}
        {health.note ? (
          <p className="tabbio-health-note" style={lineStyle}>
            {health.note}
          </p>
        ) : null}
        {/* While the agent works, only the card and its line stay. */}
        {!working ? (
          <>
            {health.suggestions.length > 0 ? (
              <HealthDisclosure
                label={health.copy.viewSuggestions}
                testID={SUGGESTIONS_TEST_ID}
                open={health.suggestionsOpen}
                onOpenChange={health.setSuggestionsOpen}
              >
                {/* Read-only rows: the problem, then what we'll do about it. */}
                <HealthSuggestionList
                  report={report}
                  findings={health.suggestions}
                  variant="onboarding"
                  subtitle="fix"
                />
              </HealthDisclosure>
            ) : null}
            <HealthDisclosure label={health.copy.whatWeChecked} rememberKey={`checked:${report.id}`}>
              <HealthCheckedAreas report={report} onOpenArea={health.openArea} />
            </HealthDisclosure>
          </>
        ) : null}
      </div>
    );
  } else if (health.agent) {
    // A fix that started before its card's report arrived (the web remount
    // after the chooser): the agent line alone, never a blank step.
    body = (
      <div className="tabbio-health-report">
        <HealthAgentStatus
          label={health.agent.label}
          context={health.agent.context}
          working
        />
      </div>
    );
  }

  return (
    <div style={rootStyle}>
      {topBar}
      <div style={scrollStyle}>
        {body ? <div style={{ ...columnStyle, ...reportStackStyle }}>{body}</div> : null}
      </div>
      {report && footer ? (
        <div style={{ ...columnStyle, ...footerStyle }}>
          {health.error ? (
            <p className="tabbio-health-error is-center" role="alert">
              {health.error}
            </p>
          ) : null}
          {footer.primary ? (
            <WebSaveButton
              label={footer.primary.label}
              onClick={footer.primary.onPress}
              loading={footer.primary.loading}
              disabled={footer.primary.disabled}
            />
          ) : null}
          {footer.secondary ? (
            <QuietLink
              label={footer.secondary.label}
              onClick={footer.secondary.onPress}
              disabled={footer.secondary.disabled}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const rootStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
};

const columnStyle: CSSProperties = {
  width: '100%',
  maxWidth: 560,
  marginInline: 'auto',
};

const topBarStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '40px minmax(0, 1fr) 40px',
  // A fixed row, so the card never jumps when Back appears after a fix.
  gridTemplateRows: '40px',
  alignItems: 'center',
  gap: 8,
  paddingBlock: '20px 8px',
  flexShrink: 0,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 17,
  lineHeight: '22px',
  fontWeight: 600,
  textAlign: 'center',
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const backButtonStyle: CSSProperties = {
  width: 40,
  height: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  borderRadius: 9999,
  background: 'none',
  cursor: 'pointer',
};

const centerStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 28,
  paddingBlock: '24px 72px',
};

const scrollStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

const reportStackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBlock: '4px 24px',
};

const resultHeadingStyle: CSSProperties = {
  margin: '0 0 8px',
  fontSize: 17,
  lineHeight: '24px',
  fontWeight: 600,
  color: 'var(--health-ink)',
  textAlign: 'center',
  textWrap: 'balance',
};

const lineStyle: CSSProperties = {
  marginBlockEnd: 12,
  fontSize: 13,
  lineHeight: '18px',
};

const footerStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingBlockEnd: 24,
};

const quietLinkStyle: CSSProperties = {
  alignSelf: 'center',
  minHeight: 44,
  marginTop: 4,
  paddingInline: 8,
  border: 0,
  background: 'none',
  font: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
};

const quietLinkDisabledStyle: CSSProperties = {
  opacity: 0.4,
  cursor: 'default',
};

export default StepCvHealth;
